(() => {
  const stateKey = 'phc-gift-draft-state-v1';
  const states = new Map();
  let queue = Promise.resolve();
  let pending = 0;
  let lastCart = null;
  let config = null;
  const root = () => window.Shopify?.routes?.root || '/';
  try {
    Object.entries(JSON.parse(sessionStorage.getItem(stateKey) || '{}')).forEach(([key, value]) => states.set(key, value));
  } catch (_) {}
  function saveState(key, state) {
    states.set(key, state);
    try { sessionStorage.setItem(stateKey, JSON.stringify(Object.fromEntries([...states].slice(-12)))); } catch (_) {}
  }
  const variantId = item => Number(item.variant_id || item.id);
  function classify(cart) {
    const items = (cart.items || []).filter(item => ![config?.giftId, config?.boxingId].includes(variantId(item)));
    const named = item => ((item.product_type || '') + ' ' + (item.product_title || item.title || '')).toLowerCase();
    const standalone = item => !/\bsets?\b|\bpacks?\b|scrunchie|bundle|gift box|\bkit\b|mommy and me|two.piece|2.piece|multi.pack/.test(named(item));
    const bonnets = items.filter(item => named(item).includes('bonnet') && standalone(item));
    const turbans = items.filter(item => named(item).includes('turban') && standalone(item));
    const robes = items.filter(item => named(item).includes('robe') && standalone(item));
    const bonnetCount = bonnets.reduce((sum, item) => sum + item.quantity, 0);
    const turbanCount = turbans.reduce((sum, item) => sum + item.quantity, 0);
    return {
      bonnetCount, turbanCount, giftEligibleCount: bonnetCount + turbanCount,
      qualifying: bonnets.filter(item => (item.handle || '').startsWith('original-stretch-tie-satin-bonnet')).reduce((sum, item) => sum + item.quantity, 0),
      robeCount: robes.reduce((sum, item) => sum + item.quantity, 0),
      accessoryCount: items.filter(item => ['satin-sleep-set-add-on-eye-mask-oversized-scrunchie', 'bonnet-eye-mask-scrunchie-add-on'].includes(item.handle)).reduce((sum, item) => sum + item.quantity, 0),
      bonnetPrice: bonnets[0] ? Number(bonnets[0].final_price ?? bonnets[0].price) : 0,
      key: items.map(item => String(variantId(item)) + ':' + item.quantity).sort().join('|')
    };
  }
  function busy(active) {
    document.body.classList.toggle('phc-cart-busy', active);
    document.querySelectorAll('button[name="checkout"]').forEach(button => {
      button.disabled = active || (lastCart && lastCart.item_count === 0);
      button.setAttribute('aria-busy', String(active));
    });
  }
  async function api(path, body) {
    const response = await fetch(root() + path, body ? {
      method: 'POST',
      headers: {'Content-Type': 'application/json'},
      body: JSON.stringify(body)
    } : {headers: {'Accept': 'application/json'}});
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || result.message || 'Please try again.');
    return result;
  }
  async function reconcile(cart) {
    const model = classify(cart);
    const giftLines = cart.items.filter(item => variantId(item) === config.giftId);
    const boxLines = cart.items.filter(item => variantId(item) === config.boxingId);
    const giftAllowed = model.giftEligibleCount > 0 && model.robeCount > 0 && config.giftAvailable;
    const updates = {};
    giftLines.forEach((item, index) => {
      const wanted = giftAllowed && index === 0 ? 1 : 0;
      if (item.quantity !== wanted) updates[item.key] = wanted;
    });
    boxLines.forEach((item, index) => {
      const wanted = model.bonnetCount >= 2 && index === 0 ? 1 : 0;
      if (item.quantity !== wanted) updates[item.key] = wanted;
    });
    (config.accessoryPairs || []).forEach(pair => {
      const sourceId = model.giftEligibleCount ? pair.regularId : pair.offerId;
      const targetId = model.giftEligibleCount ? pair.offerId : pair.regularId;
      const sources = cart.items.filter(item => variantId(item) === sourceId);
      if (!sources.length) return;
      const targets = cart.items.filter(item => variantId(item) === targetId);
      const quantity = sources.concat(targets).reduce((sum, item) => sum + item.quantity, 0);
      sources.forEach(item => { updates[item.key] = 0; });
      if (targets.length) {
        targets.forEach((item, index) => { updates[item.key] = index === 0 ? quantity : 0; });
      } else updates[String(targetId)] = quantity;
    });
    let changed = false;
    if (Object.keys(updates).length) {
      cart = await api('cart/update.js', {updates});
      changed = true;
    }
    if (giftAllowed && !cart.items.some(item => variantId(item) === config.giftId)) {
      await api('cart/add.js', {items: [{id: config.giftId, quantity: 1, properties: {
        'Gift': config.giftNote, '_PHC_offer': 'Royal Treatment'
      }}]});
      cart = await api('cart.js');
      changed = true;
    }
    const boxed = cart.items.some(item => variantId(item) === config.boxingId) ? 'Yes' : 'No';
    if ((cart.attributes?.['Box bonnets separately'] || 'No') !== boxed) {
      cart = await api('cart/update.js', {attributes: {'Box bonnets separately': boxed}});
      changed = true;
    }
    lastCart = cart;
    return {cart, changed};
  }
  function enqueue(task) {
    pending += 1;
    busy(true);
    const job = queue.catch(() => {}).then(task);
    queue = job.catch(() => {});
    return job.finally(() => {
      pending -= 1;
      if (!pending) busy(false);
    });
  }
  class PHCCartGifts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      const initial = JSON.parse(this.querySelector('[data-phc-gift-cart]').textContent);
      this.text = JSON.parse(this.querySelector('[data-phc-gift-text]').textContent);
      config = JSON.parse(this.querySelector('[data-phc-gift-config]').textContent);
      this.currency = initial.currency || 'USD';
      this.country = initial.country;
      this.boxing = this.querySelector('[data-phc-gift-boxing]');
      this.syncCart(initial);
      if (this.dataset.inlineGifts === 'true') {
        this.boxing.addEventListener('change', event => {
          event.stopPropagation();
          if (event.target.matches('[data-phc-gift-boxes]')) this.saveBoxes(event.target);
        });
        requestAnimationFrame(() => this.placeBoxing());
      }
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-phc-decline') || button.hasAttribute('data-phc-show-gifts')) {
          this.setView('gifts');
          this.querySelector('[data-phc-gift-title]')?.focus();
        } else if (button.hasAttribute('data-phc-hide-gifts') || button.hasAttribute('data-phc-dismiss')) {
          this.setView('closed');
        } else if (button.hasAttribute('data-phc-open-robe')) {
          this.openRobePicker();
        } else if (button.hasAttribute('data-phc-close-robe')) {
          this.closeRobePicker();
        } else if (button.hasAttribute('data-phc-robe-size')) {
          this.chooseRobeSize(button.dataset.phcRobeSize);
        } else if (button.hasAttribute('data-phc-robe-variant')) {
          this.chooseRobeVariant(button);
        } else if (button.hasAttribute('data-phc-accessory-variant')) {
          if (button.disabled || this.addingChoice) return;
          this.querySelector('[data-phc-gift-select="accessories"]').value = button.dataset.phcAccessoryVariant;
          this.querySelectorAll('[data-phc-accessory-variant]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
          this.updateSelectedImage('accessories', button);
          this.updatePrice('accessories');
        } else if (button.hasAttribute('data-phc-preview-gift')) {
          this.addChoice(button.dataset.phcPreviewGift, button);
        }
      });
      const robeDialog = this.querySelector('[data-phc-robe-dialog]');
      robeDialog.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeRobePicker();
      });
      robeDialog.addEventListener('click', event => {
        if (event.target !== robeDialog) return;
        const bounds = robeDialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeRobePicker();
      });
      this.querySelectorAll('details[data-phc-gift-choice]').forEach(choice => {
        choice.addEventListener('toggle', () => {
          if (!choice.open || this.dataset.inlineGifts === 'true') return;
          this.querySelectorAll('details[data-phc-gift-choice]').forEach(other => {
            if (other !== choice) other.open = false;
          });
          saveState(this.model.key, {...(states.get(this.model.key) || {}), choice: choice.dataset.phcGiftChoice});
        });
      });
      this.addEventListener('change', event => {
        event.stopPropagation();
        if (event.target.matches('[data-phc-gift-select]')) this.updatePrice(event.target.dataset.phcGiftSelect);
        if (event.target.matches('[data-phc-gift-boxes]')) this.saveBoxes(event.target);
      });
      enqueue(async () => {
        const result = await reconcile(await api('cart.js'));
        if (result.changed) window.location.reload();
        else this.syncCart(result.cart);
      }).catch(() => this.showError(this.text.cart_error));
    }
    placeBoxing() {
      if (this.dataset.inlineGifts !== 'true' || !this.isConnected || !this.boxing) return;
      const top = document.querySelector('[data-phc-boxing-slot="top"]');
      const summary = document.querySelector('[data-phc-boxing-slot="summary"]');
      const topVisible = top && getComputedStyle(top.closest('.phc-cart-top-checkout')).display !== 'none';
      const target = topVisible ? top : summary;
      if (target && this.boxing.parentElement !== target) target.replaceChildren(this.boxing);
    }
    showError(message) {
      const element = this.querySelector('[data-phc-gift-status]');
      if (element) element.textContent = message;
    }
    openRobePicker() {
      const dialog = this.querySelector('[data-phc-robe-dialog]');
      this.chooseRobeSize('adult');
      this.querySelector('[data-phc-robe-error]').textContent = '';
      dialog.hidden = false;
      if (!dialog.open) dialog.showModal();
      this.querySelector('[data-phc-open-robe]').setAttribute('aria-expanded', 'true');
      if (typeof trapFocus === 'function') trapFocus(dialog, this.querySelector('[data-phc-robe-size="adult"]'));
    }
    closeRobePicker() {
      const dialog = this.querySelector('[data-phc-robe-dialog]');
      dialog.close();
      dialog.hidden = true;
      const trigger = this.querySelector('[data-phc-open-robe]');
      trigger.setAttribute('aria-expanded', 'false');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(trigger);
      trigger.focus();
    }
    chooseRobeSize(size) {
      if (this.addingChoice) return;
      this.querySelectorAll('[data-phc-robe-size]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.phcRobeSize === size)));
      this.querySelectorAll('[data-phc-robe-group]').forEach(group => { group.hidden = group.dataset.phcRobeGroup !== size; });
      this.querySelectorAll('[data-phc-robe-fit]').forEach(fit => { fit.hidden = fit.dataset.phcRobeFit !== size; });
      this.querySelectorAll('[data-phc-robe-variant]').forEach(button => button.setAttribute('aria-pressed', 'false'));
      this.querySelector('[data-phc-gift-select="robe"]').value = '';
      const first = this.querySelector('[data-phc-robe-group="' + size + '"] [data-phc-robe-variant]:not(:disabled)');
      if (this.dataset.inlineGifts === 'true') {
        if (first) this.updateSelectedImage('robe', first);
      } else if (first) this.updateRobeImage(first);
      this.updatePrice('robe');
    }
    chooseRobeVariant(button) {
      if (button.disabled || this.addingChoice) return;
      this.querySelector('[data-phc-gift-select="robe"]').value = button.dataset.phcRobeVariant;
      this.querySelectorAll('[data-phc-robe-variant]').forEach(choice => choice.setAttribute('aria-pressed', String(choice === button)));
      this.updateSelectedImage('robe', button);
      this.updateRobeImage(button);
      this.updatePrice('robe');
    }
    updateSelectedImage(kind, button) {
      const image = this.querySelector('[data-phc-selected-image="' + kind + '"]');
      if (!image || !button.dataset.phcImageUrl) return;
      image.src = button.dataset.phcImageUrl;
      image.alt = button.dataset.phcImageAlt;
      image.hidden = false;
    }
    updateRobeImage(button) {
      const image = this.querySelector('[data-phc-robe-image]');
      if (!image || !button.dataset.phcRobeImageUrl) return;
      image.removeAttribute('srcset');
      image.src = button.dataset.phcRobeImageUrl;
      image.alt = button.dataset.phcRobeImageAlt;
    }
    syncCart(cart) {
      this.model = classify(cart);
      const sawLandingGiftOffers = cart.attributes?.['PHC Landing Gift Offers Shown'] === 'Yes' && this.model.bonnetCount > 0;
      this.currency = cart.currency || this.currency;
      this.reviewedGiftOffers = this.dataset.inlineGifts !== 'true' && (sawLandingGiftOffers || (cart.attributes?.['PHC Product Gift Offers Shown'] === 'Yes' && this.model.giftEligibleCount > 0));
      this.boxes = cart.items.some(item => variantId(item) === config.boxingId) ? 'Yes' : 'No';
      this.hidden = !this.model.giftEligibleCount || this.reviewedGiftOffers;
      this.boxing.querySelector('[data-phc-gift-boxes]').checked = this.boxes === 'Yes';
      const mode = this.model.giftEligibleCount > 0 ? 'gifts' : 'closed';
      this.render(this.reviewedGiftOffers ? 'boxing' : (states.get(this.model.key)?.view || mode));
    }
    setView(view) {
      saveState(this.model.key, {...(states.get(this.model.key) || {}), view});
      document.querySelectorAll('phc-cart-gifts').forEach(element => {
        if (element.model?.key === this.model.key) element.render(view);
      });
    }
    render(view) {
      if (this.model.bonnetCount < 2 && view === 'boxing') view = 'closed';
      if (this.reviewedGiftOffers && this.model.bonnetCount >= 2) view='boxing';
      if(!this.reviewedGiftOffers && this.model.robeCount && this.model.bonnetCount) view = view === 'closed' ? 'closed' : 'gifts';
      if (!this.model.giftEligibleCount && view === 'gifts') view = 'closed';
      if (view === 'primary' || (this.dataset.inlineGifts === 'true' && this.model.giftEligibleCount > 0)) view = 'gifts';
      this.view = view;
      this.dataset.view = view;
      this.querySelector('[data-phc-gift-box-only]').hidden = view !== 'boxing' || this.model.bonnetCount < 2 || (this.reviewedGiftOffers && this.boxes === 'Yes');
      this.querySelector('[data-phc-primary]').hidden = view !== 'primary';
      this.querySelector('[data-phc-robe-only]').hidden = view !== 'robe-only';
      this.querySelector('[data-phc-gift-collapsed]').hidden = view !== 'closed';
      this.querySelector('[data-phc-gift-options]').hidden = view !== 'gifts';
      const complete = this.model.giftEligibleCount > 0 && (this.model.robeCount > 0 || this.model.accessoryCount > 0);
      this.querySelector('[data-phc-robe-complete]').hidden = !this.model.robeCount;
      this.querySelector('[data-phc-accessories-complete]').hidden = Boolean(this.model.robeCount) || !this.model.accessoryCount;
      this.querySelector('[data-phc-gift-complete]').hidden = !complete;
      this.querySelector('[data-phc-gift-choices]').hidden = complete || !this.model.giftEligibleCount;
      this.boxing.hidden = this.model.bonnetCount < 2 || (view !== 'gifts' && view !== 'boxing') || (this.reviewedGiftOffers && this.boxes === 'Yes');
      this.querySelector('[data-phc-open-robe]').disabled = !config.giftAvailable || !this.model.giftEligibleCount;
      ['accessories', 'robe'].forEach(kind => this.updatePrice(kind));
    }
    format(price) {
      return new Intl.NumberFormat('en-US', {style: 'currency', currency: this.currency}).format(price / 100);
    }
    updatePrice(kind) {
      const select = this.querySelector('[data-phc-gift-select="' + kind + '"]');
      const selected = select.selectedOptions[0];
      const price = this.querySelector('[data-phc-price="' + kind + '"]');
      const button = this.querySelector('[data-phc-preview-gift="' + kind + '"]');
      const usable = Boolean(select.value) && !this.addingChoice && this.model.giftEligibleCount > 0 &&
        (kind === 'robe' ? config.giftAvailable : this.currency === 'USD');
      button.disabled = !usable;
      price.hidden = !usable && !(kind === 'robe' && this.dataset.inlineGifts === 'true');
      if (!usable) return;
      const total = Number(selected.dataset.price);
      const labelText = (kind === 'accessories' ? this.text.addon_price : this.text.robe_price) + ' ';
      const valueText = this.format(total);
      const suffix = kind === 'robe' && this.dataset.inlineGifts !== 'true' ? ' · ' + this.text.free_accessories : '';
      if (price.textContent === labelText + valueText + suffix) return;
      const label = document.createTextNode(labelText);
      const value = document.createElement('strong');
      value.textContent = valueText;
      const nodes = [label, value];
      if (suffix) nodes.push(document.createTextNode(suffix));
      price.replaceChildren(...nodes);
    }
    async addChoice(kind, button) {
      const select = this.querySelector('[data-phc-gift-select="' + kind + '"]');
      if (!select.value || button.disabled || this.addingChoice) return;
      const selectedId = Number(select.value);
      this.addingChoice = true;
      button.disabled = true;
      this.querySelector('[data-phc-robe-error]').textContent = '';
      try {
        await enqueue(async () => {
          const cart = await api('cart.js');
          if (!classify(cart).giftEligibleCount) throw new Error(this.text.cart_error);
          await api('cart/add.js', {items: [{id: selectedId, quantity: 1}]});
          await reconcile(await api('cart.js'));
          window.location.reload();
        });
      } catch (_) {
        this.addingChoice = false;
        this.updatePrice(kind);
        if (kind === 'robe') this.querySelector('[data-phc-robe-error]').textContent = this.text.cart_error;
        this.showError(this.text.cart_error);
      }
    }
    async saveBoxes(input) {
      const wanted = input.checked;
      input.disabled = true;
      try {
        await enqueue(async () => {
          let cart = await api('cart.js');
          const lines = cart.items.filter(item => variantId(item) === config.boxingId);
          if (wanted && classify(cart).bonnetCount < 2) throw new Error(this.text.boxes_error);
          if (wanted && !lines.length) {
            await api('cart/add.js', {items: [{id: config.boxingId, quantity: 1, properties: {
              'Service': this.text.boxes_service, '_PHC_offer': 'Separate bonnet boxes'
            }}]});
          } else if (!wanted && lines.length) {
            await api('cart/update.js', {updates: Object.fromEntries(lines.map(item => [item.key, 0]))});
          }
          cart = await api('cart/update.js', {attributes: {'Box bonnets separately': wanted ? 'Yes' : 'No'}});
          await reconcile(cart);
          window.location.reload();
        });
      } catch (_) {
        input.checked = !wanted;
        input.disabled = false;
        this.showError(this.text.boxes_error);
      }
    }
  }
  customElements.define('phc-cart-gifts', PHCCartGifts);
  const placePageBoxing = () => document.querySelectorAll('phc-cart-gifts[data-inline-gifts]').forEach(offer => offer.placeBoxing());
  document.addEventListener('DOMContentLoaded', placePageBoxing);
  window.addEventListener('resize', placePageBoxing);
  document.addEventListener('submit', event => {
    if (event.target.id === 'cart' && event.submitter?.name === 'checkout' && pending) event.preventDefault();
  });
  document.addEventListener('click', event => {
    const trigger = event.target.closest('[data-phc-open-gifts]');
    if (!trigger) return;
    event.preventDefault();
    window.location.href = root() + 'cart';
  });
})();





